import React, { useState } from "react";
import { useFormik } from "formik";
import * as yup from "yup";
import { SidePopup } from "../../../components/model-popup/side-popup";
import { CustomButton } from "../../../components/buttons/button";
import TextIconInput from "../../../components/custom-fields/input";
import ErrorMessage from "../../../components/custom-fields/error-message";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../../redux/hooks";
import { mapLocation } from "../../../redux/slices/fire";
import { getPCCLocation, getSourceLocation } from "../../../api/list";  
import { AsyncPaginateSelect } from "../../../components/select/async-paginate-select";
import { UpdateLocationMapping } from "../../../redux/slices/mapping.slice";


// --- Option & API response shapes
interface FacilityOption {
  value: string;    // facilityId
  label: string;    // facilityName
  orgId: string;
  org_uuid: string;
}

interface sourceLocationOption {
  value: string;    // facilityId
  label: string;    // facilityName
}

// --- Paginated loader
const loadFacilities: any = async (
  searchQuery: string,
  _loadedOptions: any,
  additional: any
) => {
  const perPage = 10;

  const params={
    search_value:searchQuery,
    page:additional.page,
    limit:perPage
  }
  const response = await getPCCLocation(params);
  const result = response?.result; // Access the result object from the response

  if (!result?.data) {
    return {
      options: [],
      hasMore: false,
      additional: { page: additional.page + 1 },
    };
  }

  const options: FacilityOption[] = result.data.map((f: any) => ({
    value: f.facId,  // Changed from facilityId to facId
    label: f.facilityName,
    orgId: f.orgId,
    org_uuid: f.org_uuid,
  }));

  return {
    options,
    hasMore: result.page < result.total_pages, // Use total_pages for pagination
    additional: { page: additional.page + 1 },
  };
};
const loadSourceLocation: any = async (
  searchQuery: string,
  _loadedOptions: any,
  additional: any
) => {
  const perPage = 10;

  const params={
    search_value:searchQuery,
    page:additional.page,
    limit:perPage
  }
  const response = await getSourceLocation(params);
  const result = response?.result; // Access the result object from the response

  if (!result?.data) {
    return {
      options: [],
      hasMore: false,
      additional: { page: additional.page + 1 },
    };
  }

  const options: sourceLocationOption[] = result.data.map((f: any) => ({
    value: f.source_location,  // Changed from facilityId to facId
    label: f.source_location,

  }));

  return {
    options,
    hasMore: result.page < result.total_pages, // Use total_pages for pagination
    additional: { page: additional.page + 1 },
  };
};






// --- Workaround so SidePopup accepts children
const SidePopupAny = SidePopup as React.ComponentType<any>;

export const LocationPopup = ({
  showModal,
  setShowModal,
  title,
  handleClose,
  poupDetails,
  afterChangeMappingList
}: any) => {
  const dispatch = useAppDispatch();

  const [initialFacility] = useState<FacilityOption | null>(
    poupDetails.facId
      ? {
          value: poupDetails.facId,
          label: poupDetails.pcc_location,
          orgId: poupDetails.orgId,
          org_uuid: poupDetails.org_uuid,
        }
      : null
  );


  const [initialSourceLocation] = useState<sourceLocationOption | null>(
    poupDetails.source_location
      ? {
          value: poupDetails.source_location,
          label: poupDetails.source_location,
        }
      : null
  );
  
  const formik = useFormik<{
    facility: FacilityOption | null;
    source_location_option: sourceLocationOption | null;
  }>({
    enableReinitialize: true,
    initialValues: {
      facility: initialFacility,
      source_location_option: initialSourceLocation,
    },
    validationSchema: yup.object({
        source_location_option: yup
          .object()
          .nullable()
          .required("Source location is required"),
        facility: yup
          .object()
          .nullable()
          .required("Please select a facility"),
      }),
    onSubmit: async (values) => {
      const payload = {
        facId: values?.facility?.value || '',
        orgId: values?.facility?.orgId || '',
        org_uuid: values?.facility?.org_uuid || '',
        pcc_location: values?.facility?.label || '',
        source_location: values?.source_location_option?.label || '',
      };

      if (poupDetails?.id){
        await dispatch(UpdateLocationMapping({id:poupDetails?.id,params:payload}))
        .unwrap()
        .then((res:any) => {
          if (res?.data) {

            !res?.status ? toast.error("Location mapping already exists.") : toast.success("Location updated successfully");
            setShowModal(false);
            afterChangeMappingList();
            formik.resetForm();
          } else {
            toast.error(res?.message ?? "Update failed");
          }
        })
        .catch((err:any) => {
          toast.error(err?.message ?? err ?? "Failed to update location");
        });
        
      }
      else{
        await dispatch(mapLocation(payload))
        .unwrap()
        .then((res:any) => {
          if (res?.data) {
            !res?.status ? toast.error("Location mapping already exists.") : toast.success("Location mapped successfully");
            setShowModal(false);
            afterChangeMappingList();
            formik.resetForm();
          } else {
            toast.error(res?.message ?? "Update failed");
          }
        })
        .catch((err:any) => {
          toast.error(err?.message ?? err ?? "Failed to map location");
        });
        
      }



      


    } 
  });


  return (
    <SidePopupAny
      showModal={showModal}
      handleClose={handleClose}
      title={title}
      footerStyle="flex justify-end gap-[16px] "
      footerContent={
        <>
          <CustomButton
           buttonStyle="text-[#031227] bg-white
            hover:bg-[#F5F5F5] disabled:bg-white disabled:text-[#AAADB1] 
            px-[26px] py-[12px] text-[14px] font-semibold leading-[17px] border-[#E8ECF4] "
           buttonName="Cancel"
            primary={false}
            onClick={() => {
              setShowModal(false);
              formik.resetForm();
            }}
          />
          <CustomButton
            buttonName={poupDetails.id ? "Update" : "Add"}
            buttonStyle="text-white px-[36px] py-[12px] 
            text-[14px] font-semibold leading-[17px] hover:bg-[#166B5E] disabled:bg-[#75C8BB] "
            primary
            onClick={() => formik.handleSubmit()}
            type="button"
          />
        </>
      }
    >
      <div className="space-y-4">
        {/* Source Location (read‑only) */}

        

         {/* sourc location select (async & paginated) */}
         {poupDetails.id ? (

                <div>
                <TextIconInput
                label="Source Location"
                name="source_location"
                value={formik.values.source_location_option?.label || ""}
                disable
                />
                {formik.touched.source_location_option && formik.errors.source_location_option && (
                <ErrorMessage>{formik.errors.source_location_option}</ErrorMessage>
                )}
        </div>
            
          
         ):(

            <div className="">
            <label className="font-medium text-[12px] text-[#1C2A3D] leading-[100%]">Source Location <span className="text-red-500">*</span></label>
  
          <AsyncPaginateSelect
                    name="source_location"
                    value={formik.values.source_location_option}
                    loadOptions={loadSourceLocation}
                    additional={{ page: 1 }}
                    onChange={(opt: any) => formik.setFieldValue("source_location_option", opt)}
                    placeholder="Type to search source location"
                    isClearable
                    debounceTimeout={300}
                    className="w-[99%]"
                  />
                  {formik.touched.source_location_option 
                  && formik.errors.source_location_option 
                  && (
                    <ErrorMessage>{formik.errors.source_location_option}</ErrorMessage>
                    )}
          </div>
          
         )}
        
        {/* Facility Select (async & paginated) */}
        <div className="">
          <label className="font-medium text-[12px] text-[#1C2A3D] leading-[100%]">Facility Name <span className="text-red-500">*</span></label>

        <AsyncPaginateSelect
                  name="facility"
                  value={formik.values.facility}
                  loadOptions={loadFacilities}
                  additional={{ page: 1 }}
                  onChange={(opt: any) => formik.setFieldValue("facility", opt)}
                  placeholder="Type to search facility"
                  isClearable
                  debounceTimeout={300}
                  className="w-[99%]"
                />
                {formik.touched.facility && formik.errors.facility && (
                  <ErrorMessage>{formik.errors.facility}</ErrorMessage>
                  )}
        </div>




        {/* Display the selected IDs */}
        {formik.values.facility && (
          <div className="space-y-4">
            <TextIconInput
              label="Facility ID"
              name="facility_id"
              value={formik.values.facility.value}
              disable
            />
            <TextIconInput
              label="Organization ID"
              name="org_id"
              value={formik.values.facility.orgId}
              disable
            />
          </div>
        )}
      </div>
    </SidePopupAny>
  );
};
